import { Box, Typography, Divider, IconButton, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import type {
  ValidCharacterItem,
  AvailableValidCharacterItem,
} from "../types/api-types";
import type { FormData } from "../template-configuration/template-advanced-config";
import useValidCharactersSelection from "../hooks/use-valid-characters-selection";
import ValidCharactersTable from "./valid-characters-table";
import ValidCharactersDialog from "./valid-characters-dialog";
import RemoveCharacterConfirmationDialog from "./remove-character-confirmation-dialog";

interface TemplateValidCharactersListProps {
  validCharactersList: ValidCharacterItem[];
  availableValidCharacters: AvailableValidCharacterItem[];
  isLoadingValidCharacters: boolean;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  onSaveCharacters: (
    added: ValidCharacterItem[],
    deleted: ValidCharacterItem[],
  ) => Promise<void>;
}

const TemplateValidCharactersList = ({
  validCharactersList,
  availableValidCharacters,
  isLoadingValidCharacters,
  setFormData,
  onSaveCharacters,
}: TemplateValidCharactersListProps) => {
  const {
    openDialog,
    pendingSelections,
    isSaving,
    confirmDialog,
    isDeleting,
    handleOpenDialog,
    handleCloseDialog,
    handleToggleCharacter,
    handleSaveSelections,
    handleRemoveCharacter,
    handleConfirmRemove,
    handleCancelRemove,
  } = useValidCharactersSelection({
    validCharactersList,
    setFormData,
    onSaveCharacters,
  });

  return (
    <Box>
      <Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mt: 3,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography variant="body2" fontWeight={600}>
              Valid Characters
            </Typography>
            <Chip
              label={`${validCharactersList.length} active`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.7rem" }}
            />
          </Box>
          <IconButton size="small" color="success" onClick={handleOpenDialog}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider sx={{ mb: 1 }} />

        <Box
          sx={{
            border: 1,
            borderColor: "grey.300",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <ValidCharactersTable
            characters={validCharactersList}
            isLoading={isLoadingValidCharacters}
            onRemoveCharacter={handleRemoveCharacter}
          />
        </Box>
      </Box>

      <ValidCharactersDialog
        open={openDialog}
        availableCharacters={availableValidCharacters}
        selectedCharacters={pendingSelections}
        isSaving={isSaving}
        onClose={handleCloseDialog}
        onToggleCharacter={handleToggleCharacter}
        onSave={handleSaveSelections}
      />

      <RemoveCharacterConfirmationDialog
        open={confirmDialog.open}
        character={confirmDialog.character}
        isDeleting={isDeleting}
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />
    </Box>
  );
};

export default TemplateValidCharactersList;
